/**
 * A DESIGN-REVIEW REGISTER FINDING, RENDERED FOR A PERSON.
 *
 * ## What was wrong
 *
 * `RecordFacts` fell back to `JSON.stringify` for any object-shaped claim, which is right for a band
 * or a schedule and catastrophic for a register finding — an object whose fields are themselves
 * paragraphs. Twenty-one of them rendered on The Commons as walls of
 * `{"type":"CONTRADICTION","detail":"…","triage":"MOVES_PRICING",…}`, in bold, one after another.
 * Brad, 2026-08-27: _"The comments in the Deals need to be more readable. This is a mess. These need
 * to be written like a human wrote it. Plus we have to scroll forever."_
 *
 * Three separate defects sat in that one screen and this module answers all three:
 *
 * 1. **The prose was inside the JSON.** `summary` is a finished human sentence and it was buried in
 *    punctuation. It is now the headline.
 * 2. **Machine vocabulary reached a reader.** `MOVES_PRICING`, `OPEN_QUESTION`, `CONTRADICTION`. The
 *    Canon Index rules that _"machinery notation reaching a reader who never agreed to learn it —
 *    bracketed engine notes, **register field names**, process shorthand"_ must translate at the
 *    surface. Named explicitly, and we were shipping the opposite.
 * 3. **Everything was expanded, always.** The detail and the citations are reference material, not
 *    the thing you scan a record for.
 *
 * ⚠️ The ruled term is not DISCARDED, only translated at the surface — canon's boundary is _"ruled
 * vocabulary stands in the record, and translates at an external surface"_. The raw code travels as
 * the chip's `title`, so a reader who wants the register's own word can find it and the record still
 * holds it verbatim.
 */

/**
 * The subset of a register finding this renderer needs. Mirrors `RegisterFinding` in
 * `@ggpathos/lifecycle` — `packages/web` has no workspace dependencies, so the shape is duplicated
 * rather than imported, and `register-finding.test.ts` pins the two together.
 *
 * Every field except `summary` is optional because the ingest plan makes only `summary` load-bearing:
 * a finding without one is refused at the door, so a rendered finding always has a headline.
 */
export interface RegisterFindingValue {
  readonly id?: string;
  // `null` as well as absent: the importer stores a blank register cell as null, and a client's read
  // sends null for a status outside the register's set (Bugbot, #1605).
  readonly type?: string | null;
  readonly triage?: string | null;
  readonly status?: string | null;
  readonly discipline?: string | null;
  readonly summary: string;
  readonly detail?: string | null;
  readonly citations?: readonly string[];
  readonly resolution?: string | null;
  /**
   * Must this be settled before the work can be priced? Only a client's read carries it
   * (`clientViewOfFinding`), in place of the triage it is derived from; staff read `triage` itself.
   */
  readonly settleFirst?: boolean;
  /**
   * A protected category the finding PROPOSES (ADR-0063 D2) — set by the technical review, carried
   * only at `stated`, and decided by an owner. Absent on every ordinary finding.
   */
  readonly protectedCandidate?: string;
  /**
   * The party the register (or the tracked-quantity check) named for a finding with no clock (#1602;
   * ADR-0069 A3 S8). Ruling 4 holds an owner only beside a clock, so it is kept here, not in the owner
   * column. Staff reads only: a client's view never carries it.
   */
  readonly statedOwner?: string;
  /**
   * The design review's PROPOSAL that this finding is closed (A3 S5) — written only by the review's
   * closure audit; the assert route refuses it from a person and the importer never maps it. A person
   * rules on it. Never changes `status`.
   */
  readonly closureCandidate?: {
    readonly proposed?: string;
    readonly reason?: string;
    readonly by?: readonly string[];
    readonly firstProposedIn?: string;
    /** A3 S7 — a checklist silence answered present: the item and the quote that verified in the set. */
    readonly item?: string;
    readonly quote?: string;
  };
}

/**
 * The design review's `record_facts.source` — mirrors `DESIGN_REVIEW_TOOL` in `@ggpathos/lifecycle`,
 * pinned to it by `register-finding.test.ts`. A closure candidate on a row with any other source is
 * not the review's to have proposed, and is not rendered as its proposal.
 */
export const DESIGN_REVIEW_SOURCE = 'GGPathOS design review';

/**
 * The plain sentence for a closure candidate the design review proposed, or null.
 *
 * ⚠️ Null unless the row's `source` is the design review AND the proposal is one of the two it makes:
 * "Proposed by design review" is a claim about who proposed it, so it is only shown where that is true.
 */
export function closureCandidateNote(value: unknown, source: string): string | null {
  if (source !== DESIGN_REVIEW_SOURCE || !isRegisterFinding(value)) return null;
  const c = value.closureCandidate;
  if (c === null || typeof c !== 'object') return null;
  const tail = 'A person confirms or rejects it.';
  if (c.proposed === 'ANSWERED_OR_SUPERSEDED' && typeof c.quote === 'string' && c.quote !== '') {
    // The quote EXISTS in the set; whether it answers the item is exactly what the person judges.
    const item = typeof c.item === 'string' && c.item !== '' ? `item ${c.item} ` : '';
    return `answered — a later checklist run answered ${item}present, quoting “${c.quote}”. ${tail}`;
  }
  if (c.proposed === 'ANSWERED_OR_SUPERSEDED') {
    const issue = typeof c.firstProposedIn === 'string' ? c.firstProposedIn.trim() : '';
    return `answered or superseded — not raised again${issue ? ` in ${issue}` : ''}. ${tail}`;
  }
  if (c.proposed === 'SUPERSEDED') {
    const by = Array.isArray(c.by) ? c.by.filter((k) => typeof k === 'string' && k !== '') : [];
    if (by.length === 0) return `superseded by a newer finding. ${tail}`;
    // Each one named quotes everything this one quotes, on its own — `auditClosure` names no partial
    // match. "Quotes", never "every place": matching quote text cannot prove two places are one.
    const which = by.length === 1 ? 'which quotes' : 'each of which quotes';
    return `superseded by ${by.join(', ')}, ${which} everything this one quotes. ${tail}`;
  }
  return null;
}

/** The two categories an owner can attest today (ADR-0063), in the reader's words. */
const PROTECTED_LABELS: Readonly<Record<string, string>> = {
  life_safety: 'Life safety',
  structural_integrity: 'Structural integrity',
};

/** A protected category in the reader's words, or null when it is not one an owner can attest. */
export function protectedCategoryLabel(code: string): string | null {
  return PROTECTED_LABELS[code] ?? null;
}

/**
 * The protected category a finding proposes, or null.
 *
 * ⚠️ An unrecognised value reads as null rather than being shown raw: only the two attestable
 * categories reach the record through its doors (`candidateEntryRefusal`), so anything else is a
 * malformed row, and offering a Confirm button on it would invite a 422.
 */
export function protectedCandidateOf(value: unknown): { code: string; label: string } | null {
  if (!isRegisterFinding(value)) return null;
  const code = value.protectedCandidate;
  if (typeof code !== 'string') return null;
  const label = PROTECTED_LABELS[code];
  return label ? { code, label } : null;
}

/**
 * Is this claim's value a register finding?
 *
 * ⚠️ Keyed on `summary` being a non-empty string, which is exactly the field the ingest plan requires
 * and refuses a finding for lacking. A looser check — "has a `type`" — would capture other object
 * shapes and render them through the wrong template, which is the failure the JSON fallback was
 * written to avoid in the first place. Anything that is not clearly a finding keeps that fallback.
 */
export function isRegisterFinding(value: unknown): value is RegisterFindingValue {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const summary = (value as { summary?: unknown }).summary;
  return typeof summary === 'string' && summary.trim() !== '';
}

/**
 * Turn a register code into words: `MOVES_PRICING` → `Moves pricing`, `OPEN_QUESTION` → `Open
 * question`.
 *
 * ⚠️ Mechanical rather than a lookup table, deliberately. A table would silently pass through the
 * first code Chad adds — and passing an untranslated `SUPERSEDED_BY_ISSUE` to a reader is the exact
 * defect this exists to stop. A rule that handles the shape handles codes nobody has written yet.
 *
 * Only the first word is capitalised: these are sentence fragments in a chip, not titles.
 */
export function humanise(code: string): string {
  const words = code.trim().toLowerCase().replace(/_/g, ' ');
  if (words === '') return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One labelled chip on a finding's header, carrying the register's own code for anyone who wants it. */
export interface FindingChip {
  /** The human phrase — what a reader sees. */
  readonly label: string;
  /** The register's raw code — `title` text, so the ruled term stays reachable. */
  readonly code: string;
  /** Which field it came from, for styling. `status` is the one that gets colour. */
  readonly kind: 'type' | 'status' | 'triage';
}

/**
 * The chips for one finding, in reading order: what kind of thing it is, whether it is still open,
 * and whether it moves money.
 *
 * Absent fields produce NO chip rather than an "unknown" one — a register that did not say something
 * should render as silence, not as a label asserting we asked and got nothing.
 */
export function findingChips(finding: RegisterFindingValue): FindingChip[] {
  const chips: FindingChip[] = [];
  const push = (code: string | null | undefined, kind: FindingChip['kind']) => {
    const trimmed = code?.trim();
    if (trimmed === undefined || trimmed === '') return;
    chips.push({ label: humanise(trimmed), code: trimmed, kind });
  };
  push(finding.type, 'type');
  push(finding.status, 'status');
  push(finding.triage, 'triage');
  return chips;
}

/**
 * Statuses that mean the finding still needs someone.
 *
 * `CARRIED` joined with canon's current register schema (`open | answered | closed | carried`): a
 * finding carried forward to the next issue is UNRESOLVED by definition — it was carried because it
 * was not answered. Reading only `OPEN` would show every carried finding as settled. `ACCEPTED`
 * (operator prompt v1.7-beta) stays live until the drawings show the change, and `REOPENED` is open by
 * name; a list without them counted both as having no status.
 */
const OPEN_STATUSES = new Set(['OPEN', 'CARRIED', 'ACCEPTED', 'REOPENED']);

/**
 * Whether this finding still needs someone, for the one visual signal on the row.
 *
 * ⚠️ Compared case-insensitively against the register's own word (the first register we imported says
 * `OPEN`; canon's schema now says `open`), and TRUE ONLY on an explicit open status. A finding whose
 * status the register did not state is not asserted to be open — the whole panel exists to avoid
 * claiming things the record does not say.
 */
export function isOpenFinding(finding: RegisterFindingValue): boolean {
  return OPEN_STATUSES.has(finding.status?.trim().toUpperCase() ?? '');
}
